import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import { computeRangeToggle, sameOrder } from '../lib/multiSelectRange';

const EMPTY_DISABLED: ReadonlySet<string> = new Set();

export interface UseMultiSelectOptions {
  /** Ids a click or a shift-click range must never select. Skipped inside
   *  ranges rather than force-selected; click-anywhere is a no-op on them
   *  entirely (e.g. non-DRAFT invoices in the reopen picker). */
  disabledIds?: ReadonlySet<string>;
}

export interface MultiSelectRowProps {
  onClick: (e: MouseEvent) => void;
}

export interface UseMultiSelectResult {
  /** Selection mode is "active" the moment anything is selected — the same
   *  signal that already shows the bulk action bar. Click-anywhere and the
   *  user-select suppression both follow it. */
  active: boolean;
  isSelected: (id: string) => boolean;
  /** Toggles one row. Pass `{ shiftKey: true }` (straight from the click
   *  event) to select the range from the last-toggled row instead — with no
   *  anchor yet, this is just a plain toggle. */
  toggle: (id: string, opts?: { shiftKey?: boolean }) => void;
  /** Selects `ids` (default: every non-disabled id in `orderedIds`), merged
   *  into the current selection, and drops the anchor. */
  selectAll: (ids?: readonly string[]) => void;
  /** Clears the selection and the anchor. */
  clear: () => void;
  /**
   * Row click handler for the click-anywhere pattern: a no-op while
   * inactive or when `id` is disabled, so plain navigation/row actions and
   * disabled rows are unaffected. While active, stops the event (so it
   * doesn't also activate a nested link/button, or a row's own navigation
   * handler) and toggles/ranges instead.
   *
   * Only wired to `onClick` — never touches keyboard activation, so the
   * checkbox stays focusable and Space still toggles it. Give the checkbox
   * itself (or its wrapper) its own `onClick={(e) => e.stopPropagation()}`
   * so a click on it flips the row exactly once instead of toggling twice.
   */
  getRowProps: (id: string) => MultiSelectRowProps;
  /** Spread on the list container: suppresses the text-selection side
   *  effect of shift-click, only while active. */
  containerProps: { className: string };
}

/**
 * Gmail/Finder-style multi-select: once anything is selected, clicking
 * anywhere on a row toggles it, and shift-click selects the range from the
 * last-toggled row — in the list's CURRENT rendered order. One hook shared
 * by every multi-select list in the dashboard so the three surfaces can't
 * drift (THI-180).
 *
 * Controlled: the caller owns the selection (`selected`/`onChange`), same as
 * a controlled input. This is what lets a page keep its own selection model
 * — e.g. CloseMonthPage's "excluded" set, whose default is everyone
 * selected; inverting that to a plain `selectedIds` Set would need an
 * effect to re-sync it every time the payer list loads. The hook only owns
 * the shift-click anchor.
 *
 * `orderedIds` must be the rendered order AFTER filter/sort — the hook drops
 * the anchor whenever that order changes (content, not just reference) so a
 * shift-click never ranges across a gap the teacher never saw open up.
 */
export function useMultiSelect(
  orderedIds: readonly string[],
  selected: ReadonlySet<string>,
  onChange: (next: Set<string>) => void,
  opts: UseMultiSelectOptions = {},
): UseMultiSelectResult {
  const disabledIds = opts.disabledIds ?? EMPTY_DISABLED;
  const anchorRef = useRef<string | null>(null);
  const prevOrderedIds = useRef<readonly string[]>(orderedIds);

  // Re-anchor (drop the anchor) the moment the visible order changes — a
  // shift-click must never range across a gap the teacher never saw open
  // up. Refs can't be read or written during render (react-hooks/refs), so
  // this runs as an effect; it commits before any next click can reach the
  // handler below, so there is no window where a stale anchor is usable.
  useEffect(() => {
    if (!sameOrder(prevOrderedIds.current, orderedIds)) {
      prevOrderedIds.current = orderedIds;
      anchorRef.current = null;
    }
  }, [orderedIds]);

  const active = selected.size > 0;

  function toggle(id: string, toggleOpts?: { shiftKey?: boolean }) {
    const { next, nextAnchor } = computeRangeToggle(
      orderedIds,
      disabledIds,
      selected,
      anchorRef.current,
      id,
      !!toggleOpts?.shiftKey,
    );
    anchorRef.current = nextAnchor;
    onChange(next);
  }

  function selectAll(ids?: readonly string[]) {
    const source = ids ?? orderedIds;
    const next = new Set(selected);
    for (const id of source) {
      if (!disabledIds.has(id)) next.add(id);
    }
    anchorRef.current = null;
    onChange(next);
  }

  function clear() {
    anchorRef.current = null;
    onChange(new Set());
  }

  function getRowProps(id: string): MultiSelectRowProps {
    return {
      onClick: (e) => {
        if (!active || disabledIds.has(id)) return;
        e.preventDefault();
        e.stopPropagation();
        toggle(id, { shiftKey: e.shiftKey });
      },
    };
  }

  return {
    active,
    isSelected: (id) => selected.has(id),
    toggle,
    selectAll,
    clear,
    getRowProps,
    containerProps: { className: active ? 'select-none' : '' },
  };
}
